import {
  Group,
  Burger,
  TextInput,
  ActionIcon,
  Menu,
  Avatar,
  useMantineColorScheme,
  Kbd,
  rem,
  Badge,
  Tooltip,
  Loader,
} from '@mantine/core';
import { useHotkeys, useDisclosure } from '@mantine/hooks';
import { spotlight, Spotlight } from '@mantine/spotlight';
import {
  IconSearch,
  IconBell,
  IconUser,
  IconSettings,
  IconLogout,
  IconSun,
  IconMoon,
  IconDeviceDesktop,
  IconListCheck,
  IconEye,
  IconCloud,
  IconCloudOff,
  IconRouter,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { useFogStatus, useRelayAgents } from '@/hooks';

interface HeaderProps {
  mobileOpened: boolean;
  desktopOpened: boolean;
  toggleMobile: () => void;
  toggleDesktop: () => void;
}

export function Header({
  mobileOpened,
  desktopOpened,
  toggleMobile,
  toggleDesktop,
}: HeaderProps) {
  const navigate = useNavigate();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const { user, logout } = useAuthContext();
  const { data: fogStatus, isLoading: fogLoading } = useFogStatus();
  const { data: relayAgents, isLoading: relayLoading } = useRelayAgents();

  // Count online relay agents
  const onlineAgents = relayAgents?.filter(a => a.status === 'online') || [];
  const totalAgents = relayAgents?.length || 0;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_notificationsOpened, { toggle: toggleNotifications }] = useDisclosure();

  // Global search hotkey
  useHotkeys([['mod+K', () => spotlight.open()]]);

  const spotlightActions = [
    {
      id: 'devices',
      label: 'Go to Devices',
      description: 'View and manage all devices',
      onClick: () => navigate('/devices'),
      leftSection: <IconDeviceDesktop size={18} />,
    },
    {
      id: 'preview',
      label: 'Go to Preview',
      description: 'Preview changes before applying',
      onClick: () => navigate('/preview'),
      leftSection: <IconEye size={18} />,
    },
    {
      id: 'tasks',
      label: 'Go to Tasks',
      description: 'View task status and history',
      onClick: () => navigate('/tasks'),
      leftSection: <IconListCheck size={18} />,
    },
  ];

  return (
    <>
      <Group h="100%" px="md" justify="space-between">
        {/* Left section: Burger + Logo */}
        <Group>
          <Burger
            opened={mobileOpened}
            onClick={toggleMobile}
            hiddenFrom="sm"
            size="sm"
          />
          <Burger
            opened={desktopOpened}
            onClick={toggleDesktop}
            visibleFrom="sm"
            size="sm"
          />
          <Group gap="xs">
            <img src="/logo.svg" alt="DeploySense" height={40} style={{ objectFit: 'contain' }} />
          </Group>

          {/* FOG Server Status */}
          <Tooltip
            label={
              fogLoading
                ? 'Checking FOG connection...'
                : fogStatus?.connected
                  ? 'FOG Server Connected'
                  : fogStatus?.error || 'FOG server disconnected'
            }
            withArrow
          >
            <Badge
              size="md"
              variant="light"
              color={fogLoading ? 'gray' : fogStatus?.connected ? 'green' : 'red'}
              leftSection={
                fogLoading ? (
                  <Loader size={12} color="gray" />
                ) : fogStatus?.connected ? (
                  <IconCloud size={14} />
                ) : (
                  <IconCloudOff size={14} />
                )
              }
              className={fogStatus?.connected ? 'connected-glow' : ''}
              style={{
                textTransform: 'none',
                transition: 'all 300ms ease',
              }}
            >
              {fogLoading ? 'Checking...' : fogStatus?.connected ? 'FOG Connected' : 'FOG Offline'}
            </Badge>
          </Tooltip>

          {/* Relay Agents Status */}
          <Tooltip
            label={
              relayLoading
                ? 'Loading relay agents...'
                : onlineAgents.length > 0
                  ? `Online: ${onlineAgents.map(a => `${a.name} (${a.ip_address || 'unknown IP'})`).join(', ')}`
                  : 'No relay agents online'
            }
            withArrow
          >
            <Badge
              size="md"
              variant="light"
              color={relayLoading ? 'gray' : onlineAgents.length > 0 ? 'teal' : 'orange'}
              leftSection={
                relayLoading ? (
                  <Loader size={12} color="gray" />
                ) : (
                  <IconRouter size={14} />
                )
              }
              style={{
                textTransform: 'none',
                transition: 'all 300ms ease',
              }}
            >
              {relayLoading ? 'Loading...' : `${onlineAgents.length}/${totalAgents} Agents`}
            </Badge>
          </Tooltip>
        </Group>

        {/* Center: Global search */}
        <TextInput
          placeholder="Search devices..."
          leftSection={<IconSearch size={16} />}
          rightSection={
            <Kbd size="xs" style={{ fontSize: rem(10) }}>
              ⌘K
            </Kbd>
          }
          w={400}
          visibleFrom="sm"
          onClick={() => spotlight.open()}
          readOnly
          styles={{
            input: { cursor: 'pointer' },
          }}
        />

        {/* Right section: Actions */}
        <Group gap="xs">
          {/* Mobile search button */}
          <ActionIcon
            variant="subtle"
            size="lg"
            hiddenFrom="sm"
            onClick={() => spotlight.open()}
            aria-label="Search"
          >
            <IconSearch size={20} />
          </ActionIcon>

          {/* Theme toggle */}
          <ActionIcon
            variant="subtle"
            size="lg"
            onClick={() => toggleColorScheme()}
            aria-label="Toggle color scheme"
          >
            {colorScheme === 'dark' ? (
              <IconSun size={20} />
            ) : (
              <IconMoon size={20} />
            )}
          </ActionIcon>

          {/* Notifications */}
          <ActionIcon
            variant="subtle"
            size="lg"
            onClick={toggleNotifications}
            aria-label="Notifications"
          >
            <IconBell size={20} />
          </ActionIcon>

          {/* User menu */}
          <Menu width={200} position="bottom-end" withArrow>
            <Menu.Target>
              <ActionIcon variant="subtle" size="lg" aria-label="User menu">
                <Avatar size="sm" radius="xl" color="brand">
                  <IconUser size={16} />
                </Avatar>
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label>{user?.fullName || user?.username || 'Account'}</Menu.Label>
              <Menu.Item leftSection={<IconUser size={14} />}>Profile</Menu.Item>
              <Menu.Item leftSection={<IconSettings size={14} />}>
                Settings
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item color="red" leftSection={<IconLogout size={14} />} onClick={handleLogout}>
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>

      {/* Spotlight search modal */}
      <Spotlight
        actions={spotlightActions}
        nothingFound="No results found"
        highlightQuery
        searchProps={{
          leftSection: <IconSearch size={18} />,
          placeholder: 'Search devices, pages, or actions...',
        }}
      />
    </>
  );
}
